import { useI18nContext } from '@i18n/i18n-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Close, Delete, Plus, ThreeDots } from '@components/icons/FarseerIcons';
import { Divider } from '@components/layout/Divider';
import { FlexCol, FlexRow } from '@components/layout/Flex';
import { ShadowScroll, ShadowScrollRef } from '@components/shadow-scroll/ShadowScroll';
import useTheme from '@base/useTheme';
import { ColorPicker } from '@features/colors/ColorPicker';
import { TextInput } from '@components/form';
import { Popover } from '@components/popover/Popover';
import { ContextMenu } from '@components/context-menu/ContextMenu';
import { AddLabelContainer, AddLabelWrapper, CreateLabel, LabelsCol, StyledLabel } from './Labels.style';
import { Button } from '@components/button/Button';

interface LabelsProps<T> {
    labels: T[];
    readonly?: boolean;
    colorStrategy?: 'picker' | 'random';
    getLabelId?: (label: T) => string | number;
    getLabelName: (label: T) => string;
    onAdd?: (newLabel: T) => Promise<void> | void;
    onRemove?: (label: T) => Promise<void> | void;
    onDelete?: (label: T) => Promise<void> | void;
    onUpdate?: (oldLabel: T, updatedLabel: T) => Promise<void> | void;
    onCreate?: (labelName: string, generatedColor: string) => Promise<T | undefined>;
    allLabels?: T[];
}

export function Labels<T>({
    labels,
    readonly = false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getLabelId = (label) => (label as any).id ?? JSON.stringify(label),
    getLabelName,
    colorStrategy = 'random',
    onAdd,
    onRemove,
    onDelete,
    onUpdate,
    onCreate,
    allLabels = [],
}: LabelsProps<T>) {
    const scrollRef = useRef<ShadowScrollRef>(null);

    const getLabelColor = (label: T): string => {
        if (colorStrategy === 'random') return generateLabelColor(getLabelName(label));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (label as any).hexColor;
    };

    const handleRemoveLabel = async (labelToRemove: T) => {
        if (onRemove) {
            await onRemove(labelToRemove);
        }
    };

    const handleAddLabel = async (labelToAdd: T) => {
        if (onAdd) {
            await onAdd(labelToAdd);
            scrollRef.current?.scrollToEnd();
        }
    };

    return (
        <FlexRow $align="center" $gap={4} $minWidth="0">
            <ShadowScroll ref={scrollRef} direction="horizontal">
                {labels.map((label) => (
                    <LabelComponent
                        key={getLabelId(label)}
                        name={getLabelName(label)}
                        color={getLabelColor(label)}
                        onRemove={!readonly ? () => handleRemoveLabel(label) : undefined}
                    />
                ))}
            </ShadowScroll>

            {!readonly && (
                <AddLabelPopover<T>
                    labels={allLabels}
                    getLabelId={getLabelId}
                    getLabelName={getLabelName}
                    onAdd={handleAddLabel}
                    onDelete={onDelete}
                    onUpdate={onUpdate ?? (() => {})}
                    onCreate={onCreate}
                    colorStrategy={colorStrategy}
                    allLabels={allLabels}
                />
            )}
        </FlexRow>
    );
}

interface AddLabelPopoverProps<T> {
    labels: T[];
    getLabelId: (label: T) => string | number;
    getLabelName: (label: T) => string;
    onAdd?: (label: T) => void;
    onDelete?: (label: T) => void;
    onUpdate?: (oldLabel: T, newLabel: T) => void;
    onCreate?: (labelName: string, generatedColor: string) => Promise<T | undefined>;
    colorStrategy?: 'picker' | 'random';
    allLabels: T[];
}

function AddLabelPopover<T>({
    labels,
    getLabelId,
    getLabelName,
    onAdd,
    onDelete,
    onUpdate,
    onCreate,
    colorStrategy,
    allLabels,
}: AddLabelPopoverProps<T>) {
    const { LL } = useI18nContext();
    const { colors } = useTheme();
    const [searchQuery, setSearchQuery] = useState('');
    const [editingLabel, setEditingLabel] = useState<T | null>(null);
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLElement | null>(null);

    const filteredLabels = useMemo(() => {
        return labels.filter((label) => getLabelName(label).toLowerCase().includes(searchQuery.toLowerCase()));
    }, [labels, searchQuery, getLabelName]);

    const disabledColor = colors.neutral.w300;

    const createDisabled =
        searchQuery.trim().length < 1 || allLabels.some((label) => getLabelName(label) === searchQuery);

    const handleCreateLabel = async () => {
        const trimmed = searchQuery.trim();
        if (!trimmed || createDisabled) return;

        if (onCreate) {
            const createdLabel = await onCreate(trimmed, generateLabelColor(trimmed));
            if (createdLabel) {
                await onAdd?.(createdLabel);
            }
        } else if (onAdd) {
            await onAdd?.(trimmed as T);
        }
        setSearchQuery('');
        setOpen(false);
    };

    return (
        <Popover placement="bottom-start">
            <Popover.Trigger>
                <Button type="button" icon={Plus} $type="quaternary" $size="small"></Button>
            </Popover.Trigger>
            <Popover.Content $padding="8px" $borderRadius={8}>
                <AddLabelContainer>
                    <FlexCol $gap={16}>
                        <FlexRow style={{ padding: 1, width: '100%' }}>
                            <TextInput
                                maxLength={18}
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder={LL.CREATE_OR_SEARCH()}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleCreateLabel();
                                }}
                            />
                        </FlexRow>
                        <LabelsCol style={{ height: '116px' }}>
                            {filteredLabels.map((label) => (
                                <AddLabelWrapper key={getLabelId(label)}>
                                    <LabelComponent
                                        name={getLabelName(label)}
                                        color={
                                            colorStrategy === 'random'
                                                ? generateLabelColor(getLabelName(label))
                                                : // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                  (label as any)?.hexColor
                                        }
                                        onAdd={() => {
                                            onAdd?.(label);
                                        }}
                                    />
                                    <div style={{ margin: '-4px -4px', cursor: 'pointer' }}>
                                        <ThreeDots
                                            onClick={(e: React.MouseEvent) => {
                                                setEditingLabel(label);
                                                anchorRef.current = e.currentTarget as HTMLElement;
                                                setOpen(true);
                                            }}
                                        />
                                    </div>
                                </AddLabelWrapper>
                            ))}
                        </LabelsCol>
                    </FlexCol>

                    <Divider />

                    <CreateLabel onClick={handleCreateLabel}>
                        <div style={{ paddingTop: '4px', color: colors.neutral.w700 }}>{LL.CREATE()}</div>
                        <StyledLabel $color={createDisabled ? disabledColor : generateLabelColor(searchQuery.trim())}>
                            {searchQuery.trim()}
                        </StyledLabel>
                    </CreateLabel>
                    {editingLabel && (
                        <EditLabelPopover<T>
                            editingLabel={editingLabel}
                            getLabelName={getLabelName}
                            onUpdate={onUpdate ?? (() => {})}
                            onDelete={(label) => onDelete?.(label)}
                            colorStrategy={colorStrategy}
                            open={open}
                            setOpen={setOpen}
                            anchorRef={anchorRef}
                        />
                    )}
                </AddLabelContainer>
            </Popover.Content>
        </Popover>
    );
}
interface EditLabelPopoverProps<T> {
    editingLabel: T;
    getLabelName: (label: T) => string;
    onUpdate: (oldLabel: T, updatedLabel: T) => void;
    onDelete: (label: T) => void;
    colorStrategy?: 'picker' | 'random';
    open: boolean;
    setOpen: (open: boolean) => void;
    anchorRef: React.RefObject<HTMLElement>;
}

function EditLabelPopover<T>({
    editingLabel,
    getLabelName,
    onUpdate,
    onDelete,
    colorStrategy,
    open,
    setOpen,
    anchorRef,
}: EditLabelPopoverProps<T>) {
    const { LL } = useI18nContext();
    const [newName, setNewName] = useState(getLabelName(editingLabel));

    useEffect(() => {
        if (editingLabel) {
            setNewName(getLabelName(editingLabel));
        }
    }, [editingLabel, getLabelName]);

    const handleUpdateLabel = async (updatedFields: Partial<T> | string) => {
        if (!editingLabel || !onUpdate) return;

        let updatedLabel: T;

        if (typeof editingLabel === 'string') {
            updatedLabel = updatedFields as T;
        } else {
            updatedLabel = {
                ...editingLabel,
                ...(updatedFields as Partial<T>),
            } as T;
        }
        await onUpdate(editingLabel, updatedLabel);
        setOpen(false);
    };

    return (
        <ContextMenu anchorRef={anchorRef} placement="right-end" open={open} setOpen={setOpen}>
            <ContextMenu.Item
                item={{
                    render: () => (
                        <>
                            {colorStrategy === 'picker' && (
                                <div style={{ cursor: 'pointer' }}>
                                    <ColorPicker
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        value={(editingLabel as any)?.hexColor}
                                        onChange={(c) => {
                                            handleUpdateLabel({
                                                hexColor: c,
                                            } as unknown as Partial<T>);
                                        }}
                                    />
                                </div>
                            )}
                            <FlexRow style={{ padding: 1, width: '100%' }}>
                                <TextInput
                                    maxLength={18}
                                    value={newName}
                                    onChange={setNewName}
                                    onKeyDown={(e) => {
                                        if (e.key !== 'Enter') return;

                                        handleUpdateLabel(
                                            typeof editingLabel === 'string'
                                                ? newName
                                                : ({ name: newName } as unknown as Partial<T>)
                                        );
                                        setOpen(false);
                                        setNewName('');
                                    }}
                                />
                            </FlexRow>
                        </>
                    ),
                }}
            />
            <ContextMenu.Divider />
            <ContextMenu.Item
                item={{
                    icon: Delete,
                    label: LL.DELETE(),
                    action: () => {
                        onDelete(editingLabel);
                        setOpen(false);
                    },
                }}
            />
        </ContextMenu>
    );
}
interface LabelComponentProps {
    name: string;
    color: string;
    onRemove?: () => void;
    onAdd?: () => void;
}

function LabelComponent({ name, color, onRemove, onAdd }: LabelComponentProps) {
    return (
        <StyledLabel $color={color} $removable={!!onRemove} $pointer={!!onAdd} onClick={onAdd}>
            {name}
            {onRemove && (
                <Close
                    cursor="pointer"
                    color={color}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                />
            )}
        </StyledLabel>
    );
}

function generateLabelColor(label: string): string {
    let hash = 5381;
    for (let i = 0; i < label.length; i++) {
        hash = (hash * 33) ^ label.charCodeAt(i);
    }

    // Create color components from the hash, using different bits for each component
    const r = (hash >> 16) & 0xff; // Red component
    const g = (hash >> 8) & 0xff; // Green component
    const b = hash & 0xff; // Blue component

    // random offset to diversify the colors
    const offset = 50;
    const rFinal = (r + offset) % 256;
    const gFinal = (g + offset) % 256;
    const bFinal = (b + offset) % 256;

    // color in hex format
    return `#${rFinal.toString(16).padStart(2, '0')}${gFinal.toString(16).padStart(2, '0')}${bFinal
        .toString(16)
        .padStart(2, '0')}`;
}
